import { memo, Fragment, forwardRef, useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import moment from 'moment'
import { isEmpty, throttle } from 'lodash'
import classNames from 'classnames'
import Autocomplete, { useVirtualizedListbox, createFilterOptions } from 'beam/dist/next/Autocomplete'
import TextField from 'beam/dist/TextField'
import { Typography } from 'beam/dist/Typography'
import { CircularProgress } from 'beam/dist/Progress'
import EvaluationComments from './evaluationComments'
import AppealForm from './appealForm'
import { EVAL_STATE, SCORE_TYPE } from './evaluationForm.constants'
import EvaluationSection from './EvaluationSection'
import useEvaluationFormStyles from './EvaluationForm.styles'
import { dataTestIdPrefix } from './EvaluationPanel'
import { difference } from '../../../Common/Utils'
import { DraftComment, Eval, EvalComment, EvalForm } from './evaluationForm.api'
import { IndexedChatBubble } from '../ChatMapPanel/constants'

interface EvaluationFormProps {
  values: Eval | null | undefined,
  template: EvalForm | null | undefined,
  comments: EvalComment[] | null | undefined,
  activeForms: $TSFIXME[],
  selectedForm: $TSFIXME,
  sectionsBeingUpdated: $TSFIXME[],
  callDuration: number,
  userTimezone: string,
  isCalibrationView: boolean,
  isEvaluationScored: boolean,
  isSavingValues: boolean,
  showAppealForm: boolean,
  onAppealSubmit: (...args: $TSFIXME[]) => $TSFIXME,
  onAppealCancel: (...args: $TSFIXME[]) => $TSFIXME,
  onActiveFormChange: (...args: $TSFIXME[]) => $TSFIXME,
  onGetCallTime: (...args: $TSFIXME[]) => $TSFIXME,
  onCommentAdd: (...args: $TSFIXME[]) => $TSFIXME,
  onCommentUpdate: (comment: DraftComment, ref: string) => void,
  onAnswerClick: (...args: $TSFIXME[]) => $TSFIXME,
  agentDisplayName: string,
  contactDateTime: number,
  contactId: number,
  indexedChatBubbles: IndexedChatBubble[],
}

const EvaluationForm = forwardRef<HTMLDivElement, EvaluationFormProps>(function EvaluationForm(
  {
    values,
    template,
    comments,
    activeForms,
    selectedForm,
    sectionsBeingUpdated,
    callDuration,
    userTimezone,

    isCalibrationView,
    isEvaluationScored,
    isSavingValues,
    showAppealForm,

    onAppealSubmit,
    onAppealCancel,
    onActiveFormChange,
    onGetCallTime,
    onCommentAdd,
    onCommentUpdate,
    contactId,
    agentDisplayName,
    contactDateTime,
    onAnswerClick,
    indexedChatBubbles,
  }, ref) {
  const { t } = useTranslation()
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const [maxListboxHeight, setMaxListboxHeight] = useState(0)
  const listboxComponent = useVirtualizedListbox()
  const classes = useEvaluationFormStyles({ maxListboxHeight })

  useEffect(() => {
    if (!anchorEl) return
    const updateHeight = () => {
      const rect = anchorEl.getBoundingClientRect()
      const spaceBelow = window.innerHeight - rect.bottom - 25 // 25px for footer
      const listHeight = Math.max(100, spaceBelow)
      setMaxListboxHeight(listHeight)
    }
    updateHeight()
    // Throttle the resize observer to avoid performance issues
    const throttledUpdate = throttle(updateHeight, 100)
    const observer = new ResizeObserver(throttledUpdate)
    observer.observe(anchorEl)
    // Also listen to window resize
    window.addEventListener('resize', throttledUpdate)

    return () => {
      observer.disconnect()
      window.removeEventListener('resize', throttledUpdate)
    }
  }, [anchorEl])

  const renderComments = (comments: EvalComment[], testIdPrefix: string, leaveCommentMessage: string, sectionId: number | null = null, questionId: number | null = null) => (
    // @ts-expect-error TODO: remove withAuthenticatedUser HOC from EvaluationComments or fix the types on the HOC
    <EvaluationComments
      comments={comments}
      testIdPrefix={testIdPrefix}
      leaveCommentMessage={leaveCommentMessage}
      sectionId={sectionId}
      questionId={questionId}
      callDuration={callDuration}
      // @ts-expect-error TODO: handle nullish values
      disabled={values.state.stateId === EVAL_STATE.UNSCORED}
      onGetCallTime={onGetCallTime}
      onCommentAdd={onCommentAdd}
      onCommentUpdate={onCommentUpdate}
      timezone={userTimezone}
      indexedChatBubbles={indexedChatBubbles}
    />
  )

  return (
    <div className={classes.formWrapper} ref={ref}>
      <div className={classes.header} >
        {/* @ts-expect-error TODO: handle nullish values */}
        {!isCalibrationView && (values === null || values.state.stateId === EVAL_STATE.UNSCORED) ? (
          <Autocomplete
            id="choose-eval"
            aria-label="choose-eval"
            options={activeForms}
            onChange={(e, selectedEvalForm) => onActiveFormChange(selectedEvalForm)}
            value={selectedForm}
            disableClearable
            disablePortal
            renderInput={params => (
              <TextField
                {...params}
                inputRef={node => {
                  if (node) {
                    setAnchorEl(node)
                  }
                }}
                inputProps={{
                  ...params.inputProps,
                  'data-testid': `${dataTestIdPrefix}select-eval-form`,
                }}
                label={t('chooseEvaluation')}
                fullWidth={true}
              />
            )}
            // @ts-expect-error TODO: migrate to MUI v5
            ListboxComponent={listboxComponent}
            renderOption={option => <Typography noWrap>{option.name}</Typography>}
            getOptionLabel={option => option.name}
            getOptionSelected={(option, value) => option.name === value.name}
            filterOptions={createFilterOptions({ stringify: option => option.name })}
            classes = {{ listbox: classes.listBox }}
          />
        ) : template !== null && (
          <Fragment>
            <div className={classes.contactInfo}>
              <Typography variant="subtitle2">
                {`${t('contact')} ${contactId}`} {agentDisplayName} {contactDateTime}
              </Typography>
            </div>
            <div className={classes.sectionTitle}>
              <Typography variant="body2" noWrap>
                {/* @ts-expect-error TODO: handle nullish template */}
                {template.name}
              </Typography>
              {/* @ts-expect-error TODO: fix this error */}
              {(![EVAL_STATE.UNSCORED, EVAL_STATE.CANNOT_SCORE].includes(values.state.stateId) && typeof values.totalScore !== 'undefined') && (
                <div className={classes.sectionScore}>
                  <Typography variant="caption">{isEvaluationScored ? t('finalScore') : t('currentScore')}</Typography>
                  <Typography
                    variant="body2"
                    className={classNames(
                      classes.score,
                      (isSavingValues && sectionsBeingUpdated.length > 0) && classes.scoreHide,
                    )}
                    auto-qa-id="Evaluation-Form-Score"
                    data-testid="Evaluation-Form-Score"
                  >
                    {/* @ts-expect-error TODO: is parseFloat necessary? */}
                    {Number.parseFloat(values.totalScore).toFixed(2)}
                    {/* @ts-expect-error TODO: handle nullish template */}
                    {template.scoreType.name === 'Percentage' && '%'}
                  </Typography>
                  {(isSavingValues && sectionsBeingUpdated.length > 0) && <CircularProgress className={classes.scoreProgress} size={18} />}
                </div>
              )}
            </div>
            {/* @ts-expect-error TODO: handle nullish values */}
            {values.state.stateId === EVAL_STATE.CANNOT_SCORE && (
              <div className={classes.notification}>
                <Typography variant="caption">{t('cannotScoreDesc')}</Typography>
              </div>
            )}
            {/* @ts-expect-error TODO: handle nullish values */}
            {values.responseState && values.responseState !== null && values.responseState.stateId !== 1 && (
              <div className={classes.notification}>
                <Typography variant="caption">{`${
                  // @ts-expect-error TODO: handle nullish values
                  values.responseState.stateId === 2 ? t('acknowledgedBy') : t('appealedBy')
                  // @ts-expect-error TODO: handle nullish values
                } ${values.agent.displayId} ${moment(values.responseDate).format('L')}`}</Typography>
              </div>
            )}
          </Fragment>
        )}
      </div>
      {template !== null && (
        <div className={classes.body}>
          {showAppealForm ? (
            <AppealForm onCancel={onAppealCancel} onSubmitAppeal={onAppealSubmit} />
          ) : (
            <Fragment>
              <div className={classes.formComments}>
                {/* @ts-expect-error TODO: handle nullish comments */}
                {renderComments(comments.formComments, 'evaluation-form', t('addAFormComment'))}
              </div>
              {/* @ts-expect-error TODO: handle nullish template */}
              {template.sections.map((section, index) => (
                <EvaluationSection
                  key={section.id}
                  sectionTemplate={section}
                  sectionIndex={index}
                  // @ts-expect-error TODO: handle nullish values
                  sectionValues={values.sections.find(({ id }) => id === section.id)}
                  // @ts-expect-error TODO: handle nullish template
                  isPercentageScoreType={template.scoreType.id === SCORE_TYPE.PERCENTAGE}
                  // @ts-expect-error TODO: handle nullish values
                  evaluationState={values.state.stateId}
                  comments={comments}
                  isSavingValues={isSavingValues && sectionsBeingUpdated.includes(section.id)}
                  renderComments={renderComments}
                  onAnswerClick={onAnswerClick}
                />
              ))}
            </Fragment>
          )}
        </div>
      )}
    </div>
  )
})

export default memo(EvaluationForm, (prevProps, nextProps) => {
  const diff = difference(nextProps, prevProps)
  return isEmpty(diff)
})
